import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getInsights,
  getInsightsSummary,
  getInsightsTrends,
  getWebsiteStats,
  getDmStats,
  type InsightsSummary,
  type Insight,
  type DailyTrend,
  type WebsiteStats,
} from '../api/insights';
import { useAccounts } from './useAccounts';

interface AggregatedFilters {
  selectedAccountId: string; // '' = all accounts
  dateRange: string;
  startDate: string;
  endDate: string;
}

// Within a session cached results stay fresh until the user hits Refresh.
// 30 min is long enough that account-switches and date-picker tweaks
// re-use cached data, while still expiring sometime if the tab is left open.
const STALE_TIME = 1000 * 60 * 30;

/**
 * Website / אתר KPIs — Zoho-only, account-agnostic. Reads from the dashboard
 * snapshot when the selected range is inside the 60-day window; otherwise
 * falls back to the live `/insights/website-stats` endpoint.
 */
export function useWebsiteStats(filters: {
  dateRange: string;
  startDate: string;
  endDate: string;
}): {
  data: WebsiteStats | undefined;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['website-stats', filters],
    queryFn: () =>
      getWebsiteStats({
        dateRange: filters.dateRange,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    staleTime: STALE_TIME,
  });
  return { data: q.data, isLoading: q.isLoading };
}

/** DM / WhatsApp manual lead KPIs — same shape as Website. Always live. */
export function useDmStats(filters: {
  dateRange: string;
  startDate: string;
  endDate: string;
}): {
  data: WebsiteStats | undefined;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['dm-stats', filters],
    queryFn: () =>
      getDmStats({
        dateRange: filters.dateRange,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    staleTime: STALE_TIME,
  });
  return { data: q.data, isLoading: q.isLoading };
}

/**
 * Aggregate multiple InsightsSummary objects into one
 */
function aggregateSummaries(summaries: InsightsSummary[]): InsightsSummary {
  const totalSpend = summaries.reduce((sum, s) => sum + s.totalSpend, 0);
  const totalLeads = summaries.reduce((sum, s) => sum + s.totalLeads, 0);
  const totalDeals = summaries.reduce((sum, s) => sum + s.totalDeals, 0);
  const totalRevenue = summaries.reduce((sum, s) => sum + s.totalRevenue, 0);
  const totalImpressions = summaries.reduce((sum, s) => sum + s.totalImpressions, 0);
  const totalClicks = summaries.reduce((sum, s) => sum + s.totalClicks, 0);
  const totalReach = summaries.reduce((sum, s) => sum + s.totalReach, 0);

  return {
    totalSpend,
    totalLeads,
    totalDeals,
    totalRevenue,
    totalImpressions,
    totalClicks,
    totalReach,
    avgCpl: totalLeads > 0 ? totalSpend / totalLeads : null,
    avgCostPerDeal: totalDeals > 0 ? totalSpend / totalDeals : null,
    avgRoas: totalSpend > 0 ? totalRevenue / totalSpend : null,
    avgCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
    avgCpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
    avgCpm: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
  };
}

/**
 * Aggregate trends from multiple accounts - combine daily data
 */
function aggregateTrends(allTrends: { daily: DailyTrend[] }[]): { daily: DailyTrend[] } {
  // Get all daily entries and aggregate them by date
  const dailyMap = new Map<string, DailyTrend>();

  for (const trends of allTrends) {
    for (const day of trends.daily) {
      const existing = dailyMap.get(day.date);
      if (existing) {
        existing.totalSpend += day.totalSpend;
        existing.totalLeads += day.totalLeads;
        existing.totalDeals += day.totalDeals;
        existing.totalRevenue += day.totalRevenue;
      } else {
        dailyMap.set(day.date, { ...day });
      }
    }
  }

  return { daily: Array.from(dailyMap.values()) };
}

/**
 * Hook for aggregated summary — handles single account or all accounts.
 *
 * Reads from the live `/insights/summary` endpoint per account. Same data
 * source the Insights table uses, so the two views can't disagree on
 * leads / deals / revenue / spend — any name-filter change applies
 * everywhere on the next fetch. TanStack staleTime keeps repeat reads
 * inside the 30-min window instant.
 */
export function useAggregatedSummary(filters: AggregatedFilters) {
  const { data: accounts } = useAccounts();
  const isAllAccounts = !filters.selectedAccountId;

  const singleQuery = useQuery({
    queryKey: ['insights-summary', { accountId: filters.selectedAccountId, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
    queryFn: () => getInsightsSummary({
      accountId: filters.selectedAccountId,
      dateRange: filters.dateRange,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: !isAllAccounts && !!filters.selectedAccountId,
    staleTime: STALE_TIME,
  });

  const allAccountQueries = useQueries({
    queries: (isAllAccounts && accounts?.length)
      ? accounts.map((account) => ({
          queryKey: ['insights-summary', { accountId: account._id, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
          queryFn: () => getInsightsSummary({
            accountId: account._id,
            dateRange: filters.dateRange,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }),
          staleTime: STALE_TIME,
        }))
      : [],
  });

  if (!isAllAccounts) {
    return {
      data: singleQuery.data,
      isLoading: singleQuery.isLoading,
      refetch: singleQuery.refetch,
    };
  }

  const isLoading = allAccountQueries.some((q) => q.isLoading);
  const allData = allAccountQueries
    .filter((q) => q.data)
    .map((q) => q.data as InsightsSummary);

  return {
    data: allData.length > 0 ? aggregateSummaries(allData) : undefined,
    isLoading,
    refetch: () => {
      allAccountQueries.forEach((q) => q.refetch());
    },
  };
}

/**
 * Hook for aggregated trends - handles single account or all accounts
 */
export function useAggregatedTrends(filters: AggregatedFilters) {
  const { data: accounts } = useAccounts();
  const isAllAccounts = !filters.selectedAccountId;

  // Single account query
  const singleQuery = useQuery({
    queryKey: ['insights-trends', { accountId: filters.selectedAccountId, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
    queryFn: () => getInsightsTrends({
      accountId: filters.selectedAccountId,
      dateRange: filters.dateRange,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: !isAllAccounts && !!filters.selectedAccountId,
    staleTime: STALE_TIME,
  });

  // All accounts queries
  const allAccountQueries = useQueries({
    queries: (isAllAccounts && accounts?.length)
      ? accounts.map((account) => ({
          queryKey: ['insights-trends', { accountId: account._id, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
          queryFn: () => getInsightsTrends({
            accountId: account._id,
            dateRange: filters.dateRange,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }),
          staleTime: STALE_TIME,
        }))
      : [],
  });

  if (!isAllAccounts) {
    return {
      data: singleQuery.data,
      isLoading: singleQuery.isLoading,
    };
  }

  // Aggregate all account results
  const isLoading = allAccountQueries.some((q) => q.isLoading);
  const allData = allAccountQueries
    .filter((q) => q.data)
    .map((q) => q.data as { daily: DailyTrend[] });

  return {
    data: allData.length > 0 ? aggregateTrends(allData) : undefined,
    isLoading,
  };
}

/**
 * Hook for aggregated insights list - handles single account or all accounts.
 * Each returned insight carries `accountId` so callers can key annotations
 * per (accountId, adId).
 */
export function useAggregatedInsights(filters: AggregatedFilters) {
  const { data: accounts } = useAccounts();
  const isAllAccounts = !filters.selectedAccountId;

  // Single account query
  const singleQuery = useQuery({
    queryKey: ['insights', { accountId: filters.selectedAccountId, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
    queryFn: () => getInsights({
      accountId: filters.selectedAccountId,
      dateRange: filters.dateRange,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: !isAllAccounts && !!filters.selectedAccountId,
    staleTime: STALE_TIME,
  });

  // All accounts queries
  const allAccountQueries = useQueries({
    queries: (isAllAccounts && accounts?.length)
      ? accounts.map((account) => ({
          queryKey: ['insights', { accountId: account._id, dateRange: filters.dateRange, startDate: filters.startDate, endDate: filters.endDate }],
          queryFn: () => getInsights({
            accountId: account._id,
            dateRange: filters.dateRange,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }),
          staleTime: STALE_TIME,
        }))
      : [],
  });

  if (!isAllAccounts) {
    const tagged = (singleQuery.data ?? []).map((i) => ({
      ...i,
      accountId: filters.selectedAccountId,
    }));
    return {
      data: singleQuery.data ? tagged : undefined,
      isLoading: singleQuery.isLoading,
    };
  }

  // Combine all insights into single array, tagging each with its source accountId
  const isLoading = allAccountQueries.some((q) => q.isLoading);
  const allInsights: (Insight & { accountId: string })[] = [];

  accounts?.forEach((account, index) => {
    const query = allAccountQueries[index];
    if (query?.data) {
      for (const i of query.data) {
        allInsights.push({ ...i, accountId: account._id });
      }
    }
  });

  return {
    data: allInsights.length > 0 ? allInsights : undefined,
    isLoading,
  };
}

/**
 * Insight with account information for performers display
 */
export interface InsightWithAccount extends Insight {
  accountId: string;
  accountName: string;
}

/**
 * Hook for insights with account information - for top/bottom performers
 */
export function useInsightsWithAccounts(filters: AggregatedFilters) {
  const { data: accounts, isLoading: accountsLoading } = useAccounts();
  const isAllAccounts = !filters.selectedAccountId;

  // Find account name by ID
  const getAccountName = (accountId: string): string => {
    const account = accounts?.find((a) => a._id === accountId);
    return account?.name || 'Unknown';
  };

  // Create a cache-friendly key without selectedAccountId to allow cache sharing
  const createQueryKey = (accountId: string) => [
    'insights',
    {
      accountId,
      dateRange: filters.dateRange,
      startDate: filters.startDate,
      endDate: filters.endDate,
    },
  ];

  // Single account query
  const singleQuery = useQuery({
    queryKey: createQueryKey(filters.selectedAccountId),
    queryFn: () => getInsights({
      accountId: filters.selectedAccountId,
      dateRange: filters.dateRange,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: !isAllAccounts && !!filters.selectedAccountId && !accountsLoading,
    staleTime: STALE_TIME,
  });

  // All accounts queries
  const allAccountQueries = useQueries({
    queries: (isAllAccounts && accounts?.length)
      ? accounts.map((account) => ({
          queryKey: createQueryKey(account._id),
          queryFn: () => getInsights({
            accountId: account._id,
            dateRange: filters.dateRange,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }),
          staleTime: STALE_TIME,
        }))
      : [],
  });

  // Handle single account mode
  if (!isAllAccounts) {
    // Wait for accounts to load to get account name
    if (accountsLoading) {
      return { data: undefined, isLoading: true };
    }

    const insightsWithAccount: InsightWithAccount[] = (singleQuery.data || []).map((insight) => ({
      ...insight,
      accountId: filters.selectedAccountId,
      accountName: getAccountName(filters.selectedAccountId),
    }));

    return {
      data: insightsWithAccount.length > 0 ? insightsWithAccount : undefined,
      isLoading: singleQuery.isLoading,
    };
  }

  // Combine all insights with account info
  const isLoading = accountsLoading || allAccountQueries.some((q) => q.isLoading);
  const allInsights: InsightWithAccount[] = [];

  accounts?.forEach((account, index) => {
    const query = allAccountQueries[index];
    if (query?.data) {
      const insightsWithAccount = query.data.map((insight) => ({
        ...insight,
        accountId: account._id,
        accountName: account.name,
      }));
      allInsights.push(...insightsWithAccount);
    }
  });

  return {
    data: allInsights.length > 0 ? allInsights : undefined,
    isLoading,
  };
}
